import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Lock, Eye, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <main className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <article className="bg-gray-800 rounded-2xl p-8 md:p-12 border border-gray-700">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Politique de Confidentialité
              </h1>
              <p className="text-gray-400 text-sm mb-8">
                Dernière mise à jour : 26 novembre 2025
              </p>
              
              <div className="prose prose-invert max-w-none">
                <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                  Galo Bet s'engage à protéger votre vie privée et vos données personnelles. Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos informations lorsque vous utilisez notre site web galo-bet.com. Nous respectons strictement le Règlement Général sur la Protection des Données (RGPD) et toutes les réglementations françaises applicables.
                </p>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center space-x-2">
                    <FileText className="text-pink-400" size={28} />
                    <span>1. Données que Nous Collectons</span>
                  </h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Lorsque vous utilisez notre site, nous pouvons collecter les types de données personnelles suivants :
                  </p>
                  <ul className="text-gray-300 space-y-2 ml-6 mb-4">
                    <li><strong>Données d'identification :</strong> nom, prénom, adresse email, numéro de téléphone lorsque vous nous contactez via le formulaire de contact.</li>
                    <li><strong>Données de navigation :</strong> adresse IP, type de navigateur, pages visitées, durée de visite, date et heure d'accès.</li>
                    <li><strong>Données de localisation :</strong> informations géographiques approximatives basées sur votre adresse IP.</li>
                    <li><strong>Cookies et technologies similaires :</strong> informations stockées dans votre navigateur pour améliorer votre expérience de navigation.</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Nous ne collectons que les données strictement nécessaires pour fournir nos services et améliorer votre expérience sur notre site. Nous ne collectons jamais de données de paiement ou d'informations financières sensibles directement.
                  </p>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center space-x-2">
                    <Eye className="text-pink-400" size={28} />
                    <span>2. Comment Nous Utilisons Vos Données</span>
                  </h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Nous utilisons vos données personnelles aux fins suivantes :
                  </p>
                  <ul className="text-gray-300 space-y-2 ml-6 mb-4">
                    <li><strong>Fournir nos services :</strong> répondre à vos demandes de contact, vous fournir des informations sur les sites de paris en ligne, et améliorer notre contenu.</li>
                    <li><strong>Analyser et améliorer :</strong> comprendre comment vous utilisez notre site pour améliorer l'expérience utilisateur et optimiser nos services.</li>
                    <li><strong>Communication :</strong> vous contacter en réponse à vos demandes ou pour vous informer de modifications importantes concernant notre service.</li>
                    <li><strong>Conformité légale :</strong> respecter nos obligations légales et réglementaires, répondre aux demandes des autorités compétentes.</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Nous ne vendons jamais vos données personnelles à des tiers. Nous ne partageons vos données qu'avec des prestataires de services de confiance qui nous aident à faire fonctionner notre site, et uniquement dans le cadre strict de leur mission de service.
                  </p>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center space-x-2">
                    <Lock className="text-pink-400" size={28} />
                    <span>3. Sécurité de Vos Données</span>
                  </h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données personnelles contre tout accès non autorisé, perte, destruction ou altération. Ces mesures incluent :
                  </p>
                  <ul className="text-gray-300 space-y-2 ml-6 mb-4">
                    <li>Chiffrement SSL/TLS pour toutes les transmissions de données</li>
                    <li>Accès restreint aux données personnelles uniquement aux personnes autorisées</li>
                    <li>Surveillance régulière de nos systèmes pour détecter toute activité suspecte</li>
                    <li>Sauvegardes régulières et sécurisées de nos données</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Malgré nos efforts, aucun système de transmission sur Internet n'est totalement sécurisé. Nous ne pouvons garantir la sécurité absolue de vos données, mais nous nous engageons à prendre toutes les mesures raisonnables pour les protéger.
                  </p>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4">4. Conservation des Données</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Nous conservons vos données personnelles uniquement pendant la durée nécessaire aux fins pour lesquelles elles ont été collectées, conformément aux obligations légales. Les données de contact sont conservées pendant 3 ans à compter du dernier contact. Les données de navigation sont conservées pour une durée maximale de 13 mois conformément à la réglementation sur les cookies.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Passé ces délais, vos données sont supprimées de manière sécurisée ou anonymisées pour être utilisées uniquement à des fins statistiques, sans possibilité de vous identifier.
                  </p>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4">5. Vos Droits</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
                  </p>
                  <ul className="text-gray-300 space-y-2 ml-6 mb-4">
                    <li><strong>Droit d'accès :</strong> vous pouvez demander à recevoir une copie de toutes les données personnelles que nous détenons à votre sujet.</li>
                    <li><strong>Droit de rectification :</strong> vous pouvez demander la correction de toute donnée inexacte ou incomplète.</li>
                    <li><strong>Droit à l'effacement :</strong> vous pouvez demander la suppression de vos données personnelles dans certaines circonstances.</li>
                    <li><strong>Droit d'opposition :</strong> vous pouvez vous opposer au traitement de vos données pour des motifs légitimes.</li>
                    <li><strong>Droit à la portabilité :</strong> vous pouvez demander à recevoir vos données dans un format structuré et couramment utilisé.</li>
                    <li><strong>Droit de limitation :</strong> vous pouvez demander la limitation du traitement de vos données dans certaines circonstances.</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Pour exercer ces droits, contactez-nous à l'adresse email : <a href="mailto:info@galo-bet.com" className="text-pink-400 hover:text-pink-300">info@galo-bet.com</a>. Nous répondrons à votre demande dans un délai d'un mois maximum.
                  </p>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4">6. Cookies</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Notre site utilise des cookies et technologies similaires pour améliorer votre expérience de navigation, analyser l'utilisation du site, et personnaliser le contenu. Pour plus d'informations sur notre utilisation des cookies, consultez notre <a href="/cookies" data-discover="true" className="text-pink-400 hover:text-pink-300">Politique des Cookies</a>.
                  </p>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4">7. Liens vers d'Autres Sites</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Notre site contient des liens vers des sites de paris en ligne et d'autres sites web tiers. Nous ne sommes pas responsables des pratiques de confidentialité ou du contenu de ces sites tiers. Nous vous encourageons à lire les politiques de confidentialité de tout site que vous visitez.
                  </p>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4">8. Modifications de Cette Politique</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Nous pouvons modifier cette politique de confidentialité de temps à autre pour refléter les changements dans nos pratiques ou pour d'autres raisons opérationnelles, légales ou réglementaires. Toute modification sera publiée sur cette page avec une mise à jour de la date de "dernière mise à jour". Nous vous encourageons à consulter régulièrement cette page pour rester informé de nos pratiques de confidentialité.
                  </p>
                </section>

                <section className="mb-10">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center space-x-2">
                    <Shield className="text-pink-400" size={28} />
                    <span>9. Contact</span>
                  </h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, vous pouvez nous contacter :
                  </p>
                  <div className="bg-gray-700 rounded-lg p-6 mb-4">
                    <p className="text-gray-300 mb-2"><strong>Email :</strong> <a href="mailto:info@galo-bet.com" className="text-pink-400 hover:text-pink-300">info@galo-bet.com</a></p>
                    <p className="text-gray-300 mb-2"><strong>Téléphone :</strong> <a href="tel:+33519286219" className="text-pink-400 hover:text-pink-300">+33 5 19 28 62 19</a></p>
                    <p className="text-gray-300"><strong>Adresse :</strong> Paris, France</p>
                  </div>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Vous avez également le droit d'introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) si vous estimez que le traitement de vos données personnelles constitue une violation du RGPD.
                  </p>
                </section>
              </div>
            </article>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;

